/* eslint-disable no-console, no-nested-ternary */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Drawer from 'Components/Ant/Drawer';
import RoundButton from 'Components/UI/RoundButton';
import LoadingOverlay from 'Components/UI/LoadingOverlay/LoadingOverlay';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { NotificationManager } from 'react-notifications';
import { removeQuery, addQueryWithReplace, addQuery } from 'Helpers/Query';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import useCollection from 'Hooks/useCollection';
import useUserContext from 'Context/UserContext';
import relationships from 'Consts/Relationships';
import {
  displayErrorMessage,
} from 'Helpers/FacetNotificationManager';
import { dispatchActionItemRefresh } from 'Hooks/useRefreshEvent';
import fr from 'facet-rest';
import { buildModalQuery } from 'Consts/ModalQueries';
import modalConsts from 'Consts/ModalNames';
import generateTaskTopicSchema from './taskTopicSchema';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
  },
  formControl: {
    margin: theme.spacing(1),
    flex: 1,
  },
  taskCountWrapper: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: theme.spacing(3),
    background: theme.palette.common.white,
    borderTop: '1px solid',
    borderTopColor: theme.palette.facet.lightGray2,
    zIndex: 1,
  },
  taskCountContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  topicsContainer: {
    margin: theme.spacing(3, 0, 12, 0),
  },
  topic: {
    padding: theme.spacing(2, 0),
    borderTop: '1px solid',
    borderTopColor: theme.palette.facet.lightGray2,
  },
  saveButton: {
    marginTop: theme.spacing(3),
  },
  label: {
    textTransform: 'capitalize',
    fontSize: '1em',
    color: theme.palette.facet.accentBlue,
  },
  cap: {
    textTransform: 'capitalize',
  },
}));

const Task = ({ task, type, addTask }) => (
  <FormControlLabel
    control={<Checkbox onChange={(_, checked) => addTask(checked, { type, ...task })} name={task.name} />}
    label={task.name}
  />
);

const TaskList = ({ type, tasks, addTask }) => {
  const classes = useStyles();
  return (
    <FormControl component="fieldset" className={classes.formControl}>
      <span className={classes.label}>{type}</span>
      <FormGroup>
        {tasks.map(task => <Task key={task.name} type={type} task={task} addTask={addTask} />)}
      </FormGroup>
    </FormControl>
  );
};

const Topic = ({ topic, addTask }) => {
  const classes = useStyles();
  return (
    <div className={classes.topic}>
      <Typography variant="h4" component="div" color="primary">
        {topic.type}
      </Typography>
      <div className={classes.root}>
        {Object.keys(topic.tasks)
          .filter(key => topic.tasks[key].length)
          .map(key => <TaskList key={key} type={key} tasks={topic.tasks[key]} addTask={addTask} />)
        }
      </div>
    </div>
  );
};

const TasksAndTodosModal = () => {
  const [isSaving, setSaving] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [clientId, setClientId] = useState(null);
  const [clientIdSecondary, setClientIdSecondary] = useState(null);
  const getTaskCount = (type) => { return type === 'planner' ? tasks.filter(t => t.type === type).length : tasks.filter(t => t.type !== 'planner').length; };
  const {
    user: { id: userId },
  } = useUserContext();
  const classes = useStyles();
  const { householdResource } = useSimpleHousehold();

  const onClose = () => {
    removeQuery('modal');
  };

  const onError = (error, message) => {
    NotificationManager.warning(message);
    console.error(error);
  };


  const { collection: accountCollection, setCollection: setAccountCollection } = useCollection();
  async function updateAccounts() {
    const collection = await householdResource?.relationship(relationships.accounts)?.link('related')?.get();
    if (collection) {
      setAccountCollection(collection);
    }

    return Promise.resolve();
  }

  // Generate schema.
  function generatedModifiedTaskTopicSchema() {
    const schema = generateTaskTopicSchema(clientId);
    const topics = {};
    schema.forEach((item) => {
      topics[item.category] = true;
    });
    const final = [];
    Object.keys(topics).forEach((topic) => {
      final.push({
        type: topic,
        tasks: {
          primaryClient: [],
          secondaryClient: [],
          planner: [],
        },
      });
    });
    schema.forEach((item) => {
      final.find(topic => topic.type === item.category).tasks[item.ownerCategory].push(item);
    });
    return final;
  }

  const [taskTopicSchema, setTaskTopicSchema] = useState(generatedModifiedTaskTopicSchema());

  useEffect(() => {
    if (householdResource) {
      householdResource.attr('clients').resources().list().forEach((client) => {
        const clientIdString = client.id();
        if (client.attr('isPrimary') === 'true') {
          setClientId(clientIdString);
        } else {
          setClientIdSecondary(clientIdString);
        }
      });
    }

    updateAccounts().catch(e => displayErrorMessage(e, e.errors || 'failed to fetch accounts'));
  }, [householdResource]);

  useEffect(() => {
    setTaskTopicSchema(generatedModifiedTaskTopicSchema());
  }, [accountCollection]);

  const addTask = (checked, task) => {
    if (checked) {
      setTasks([...tasks, task]);
    } else {
      setTasks(tasks.filter(t => t.name !== task.name));
    }
  };

  const saveTasksAndTodos = async (task, draftsCollections) => {
    const {
      name,
      type,
      priority,
      dueDate,
      description,
      showOnBlueprint,
    } = task;
    const taskClientId = ('isSecondaryClient' in task ? clientIdSecondary : clientId);
    const clientResource = householdResource.attr('clients').resources().list().find((client) => {
      return client.id() === taskClientId;
    });
    const clientActionItems = await clientResource.relationship(relationships.simpleActionItems).link('related').get();
    const householdAssignedTo = clientActionItems.meta().field('assignedTo').options().list()
      .find(opt => opt.attr('prompt') === 'Household')
      .id();

    // Action item defaults
    const actionItemDefaults = {
      name,
      description,
      status: 'todo',
      priority: priority || 'medium',
      dueDate,
      assignedTo: type !== 'planner' ? householdAssignedTo : userId.toString(),
      householdId: householdResource.id(),
      showOnBlueprint: showOnBlueprint || type !== 'planner',
    };

    let newActionItem;
    if (draftsCollections) {
      newActionItem = draftsCollections.create();
    } else {
      newActionItem = clientActionItems.create();
    }

    Object.keys(actionItemDefaults).forEach((attribute) => {
      newActionItem.attr(attribute, actionItemDefaults[attribute]);
    });

    // Create req. body from req. fields with default values
    await newActionItem.submit().catch(error => onError(error, `Error adding ${task.type} tasks and to dos`));
  };

  const save = async () => {
    setSaving(true);
    let draftsCollections;
    if (householdResource.relationship(fr.relationships.actionItemDraftGroup.name)) {
      const [actionItemDraftGroupResource] = await householdResource.relationship(fr.relationships.actionItemDraftGroup.name).link('related').get();
      draftsCollections = actionItemDraftGroupResource.attr('actionItemDrafts');
    }
    try {
      const allTasks = tasks.map((task) => {
        return saveTasksAndTodos(task, draftsCollections);
      });
      await Promise.all(allTasks); // <-- PLEASE NOTE: This is currently being implemented since we do not have an action item endpoint to POST multiple action-itmes in one payload.
      NotificationManager.success('Tasks and To Dos Saved');
      addQueryWithReplace({ refresh: 'action-item' });
      dispatchActionItemRefresh();
    } catch (error) {
      onError(error, 'Error adding tasks and to dos');
    } finally {
      setSaving(false);
      onClose();
      if (householdResource.relationship(fr.relationships.actionItemDraftGroup.name)) {
        addQuery({
          modal: buildModalQuery(modalConsts.simpleActionItemGroupModal, {}),
        });
      }
    }
  };

  return (
    <Drawer width={650} visible onClose={onClose}>
      {isSaving && <LoadingOverlay />}
      <Typography variant="h3" color="primary">
        {'Add BNS Task'}
      </Typography>
      <div className={classes.topicsContainer} test="bns-topic-container">
        {taskTopicSchema.map(topic => <Topic key={topic.type} topic={topic} addTask={addTask} />)}
      </div>
      <div className={classes.taskCountWrapper}>
        <div className={classes.taskCountContainer}>
          {
            ['client', 'planner'].map(type => (
              <Typography key={type} variant="h6" component="div" color="primary">
                <span className={classes.cap}>{type}</span> tasks: {getTaskCount(type)}
              </Typography>
            ))
          }
        </div>
        <RoundButton
          className={classes.saveButton}
          color="primary"
          disableElevation
          fullWidth
          variant="contained"
          disabled={!tasks.length}
          onClick={() => save()}
          test="add-bns-tasks"
        >
          {`+ Add BNS ${tasks.length > 1 ? 'Tasks' : 'Task'}`}
        </RoundButton>
      </div>
    </Drawer>
  );
};

TasksAndTodosModal.propTypes = {};

Topic.propTypes = {
  topic: PropTypes.object.isRequired,
  addTask: PropTypes.func.isRequired,
};

TaskList.propTypes = {
  type: PropTypes.string.isRequired,
  tasks: PropTypes.array.isRequired,
  addTask: PropTypes.func.isRequired,
};

Task.propTypes = {
  task: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  addTask: PropTypes.func.isRequired,
};

export default TasksAndTodosModal;
